import { useState, useRef, useCallback } from 'react';

/**
 * usePrepareProgress
 *
 * Single source of truth for the "preparing transfer" progress animation.
 * Encapsulates the entire fake-progress lifecycle so no screen needs to
 * re-implement the algorithm.
 *
 * Algorithm (industry-standard exponential-decay fake progress):
 *   p(n+1) = p(n) + (95 − p(n)) × k       — while real work runs
 *
 * With k = 0.04, tick = 120 ms:
 *   ~1 s  →  15 %   (looks "starting")
 *   ~3 s  →  38 %   (steady movement)
 *   ~6 s  →  62 %   (clearly slowing)
 *   ~12 s →  82 %   (asymptoting toward 95)
 *   Never reaches 95 % on its own — only finishSimulation() can push to 100.
 *
 * On completion: cubic ease-out p → 100 % over 400 ms at ~60 fps,
 * then lingers at 100 % for 250 ms before resolving.
 *
 * Usage:
 *   const { progress, startSimulation, finishSimulation, resetProgress } = usePrepareProgress();
 *
 *   // In your submit handler:
 *   setIsPreparing(true);
 *   await new Promise(r => setTimeout(r, 80)); // let React paint 0 %
 *   startSimulation();
 *   try {
 *     const result = await doRealWork();
 *     await finishSimulation();   // animates 0→100 %, resolves after linger
 *     // now transition to the result screen
 *   } catch (e) {
 *     resetProgress();
 *   } finally {
 *     setIsPreparing(false);
 *   }
 */

const TICK_MS    = 120;   // interval between fake-progress ticks
const DECAY_K    = 0.04;  // fraction of remaining distance consumed per tick
const FINISH_MS  = 400;   // cubic ease-out duration (current% → 100%)
const LINGER_MS  = 250;   // pause at 100% before resolving

export interface PrepareProgressResult {
  /** Current display value (0–100), or undefined when idle. */
  progress: number | undefined;
  /**
   * Kick off the animated fake-progress counter.
   * Call this AFTER the initial 0% state has been painted.
   */
  startSimulation: () => void;
  /**
   * Stop the fake counter and animate smoothly to 100%.
   * Returns a Promise that resolves after the 100% linger completes —
   * await it before switching to the result screen.
   */
  finishSimulation: () => Promise<void>;
  /** Abort immediately and reset to idle (e.g. on error or cancel). */
  resetProgress: () => void;
}

export function usePrepareProgress(): PrepareProgressResult {
  const [progress, setProgress] = useState<number | undefined>(undefined);

  // Refs hold mutable runtime state that shouldn't cause re-renders.
  const tickingRef  = useRef(false);
  const timerRef    = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const displayPRef = useRef(0);       // mirror of displayed value (avoids stale closures)

  const startSimulation = useCallback(() => {
    displayPRef.current = 0;
    setProgress(0);
    tickingRef.current = true;

    const tick = () => {
      if (!tickingRef.current) return;
      displayPRef.current = displayPRef.current + (95 - displayPRef.current) * DECAY_K;
      setProgress(Math.round(displayPRef.current));
      timerRef.current = setTimeout(tick, TICK_MS);
    };
    timerRef.current = setTimeout(tick, TICK_MS);
  }, []);

  const finishSimulation = useCallback((): Promise<void> => {
    // Stop the exponential ticker
    tickingRef.current = false;
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);

    const fromP = displayPRef.current;

    return new Promise<void>(resolve => {
      const t0 = Date.now();

      // Cubic ease-out at ~60 fps
      const step = () => {
        const t      = Math.min((Date.now() - t0) / FINISH_MS, 1);
        const eased  = 1 - Math.pow(1 - t, 3);
        const current = Math.round(fromP + (100 - fromP) * eased);
        setProgress(current);

        if (t < 1) {
          setTimeout(step, 16);
        } else {
          // Linger at 100 % so the user sees completion
          setTimeout(() => {
            setProgress(undefined);
            resolve();
          }, LINGER_MS);
        }
      };

      setTimeout(step, 16);
    });
  }, []);

  const resetProgress = useCallback(() => {
    tickingRef.current = false;
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    setProgress(undefined);
  }, []);

  return { progress, startSimulation, finishSimulation, resetProgress };
}
